import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { libraryNav } from '@/components/px/nav'
import { ClientDetailPanel } from '@/features/clients/components/ClientDetailPanel'
import { ClientFormDialog } from '@/features/clients/components/ClientFormDialog'
import { ClientsTable } from '@/features/clients/components/ClientsTable'
import {
  fetchClients,
  mutationErrorDismissed,
  updateClient
} from '@/features/clients/clientsSlice'
import {
  selectAllClients,
  selectClientsCache,
  selectClientsError
} from '@/features/clients/clientsSelectors'
import type { ApiClientRow } from '@/features/clients/types'
import { isApiError, toApiError, type ApiError } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * The client register (`/clients/register`, PX-W6.2, AC-5): the L2 client
 * boundaries with create, edit and archive/restore. The demo "Clients & AMS"
 * page at `/clients` is a different, untouched surface (W6.4 owns it).
 *
 * ⚠ Archive and restore PUT the COMPLETE loaded row with only `status`
 * changed — the server saves the patch wholesale, so a sparse `{status}` body
 * would null profile/contacts/commercialTerms/dataRoomRoot.
 */
export default function ClientsRegisterPage() {
  const { t } = useTranslation(['clients', 'people'])
  const dispatch = useAppDispatch()

  const rows = useAppSelector(selectAllClients)
  const cache = useAppSelector(selectClientsCache)
  const error = useAppSelector(selectClientsError)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<ApiClientRow | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [writeForbidden, setWriteForbidden] = useState(false)
  const [writeError, setWriteError] = useState<ApiError | null>(null)

  useEffect(() => {
    void dispatch(fetchClients())
  }, [dispatch])

  const onNewClicked = (): void => {
    setWriteForbidden(false)
    setWriteError(null)
    setEditingRow(null)
    setDialogOpen(true)
  }

  const onEditClicked = (row: ApiClientRow): void => {
    setWriteForbidden(false)
    setWriteError(null)
    setEditingRow(row)
    setDialogOpen(true)
  }

  const onCloseClicked = (): void => {
    setDialogOpen(false)
    setEditingRow(null)
    dispatch(mutationErrorDismissed())
  }

  const onExpandClicked = (id: string): void => {
    setExpandedId((current) => (current === id ? null : id))
  }

  const onArchiveClicked = async (row: ApiClientRow): Promise<void> => {
    const archived = row.status === 'archived'
    const confirmed = window.confirm(
      archived
        ? t('clients:archive.restoreConfirm', { name: row.name })
        : t('clients:archive.confirm', { name: row.name })
    )
    if (!confirmed) return
    setWriteForbidden(false)
    setWriteError(null)
    try {
      await dispatch(
        updateClient({
          clientId: row.id,
          // The COMPLETE editable payload with only the status flipped —
          // never a sparse body (the server's update saves the patch row
          // wholesale; only blank name/status fall back).
          input: {
            name: row.name,
            status: archived ? 'active' : 'archived',
            profile: row.profile,
            contacts: row.contacts,
            commercialTerms: row.commercialTerms,
            dataRoomRoot: row.dataRoomRoot
          }
        })
      ).unwrap()
    } catch (rejected) {
      // Instrumented fix (manual pass): an archive failure was SILENT — the
      // row just stayed Active. 403 renders the blocked panel; every other
      // failure surfaces its mapped message.
      if (isApiError(rejected) && rejected.status === 403) setWriteForbidden(true)
      else setWriteError(isApiError(rejected) ? rejected : toApiError(rejected))
    }
  }

  const expandedRow = expandedId !== null ? rows.find((row) => row.id === expandedId) : undefined

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('clients:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={libraryNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('clients:title')}</h1>
        <div className="flex items-baseline gap-x-4">
          <p className="text-sm text-muted-foreground">{t('clients:lede')}</p>
          <button
            type="button"
            onClick={onNewClicked}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('clients:new')}
          </button>
        </div>
      </header>

      {writeForbidden && (
        <div className="mt-4">
          <ForbiddenPanel requested="/{orgId}/clients/:clientId" />
        </div>
      )}
      {writeError !== null && (
        <div className="mt-4">
          <ErrorPanel error={writeError} />
        </div>
      )}

      <section className="mt-4 rounded-lg border border-border bg-card">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <div className="p-4">
            <ForbiddenPanel requested="/{orgId}/clients" />
          </div>
        ) : error !== null ? (
          <div className="p-4">
            <ErrorPanel error={error} onRetryClicked={() => void dispatch(fetchClients())} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyPanel
            title={t('clients:empty.title')}
            body={t('clients:empty.body')}
            actionLabel={t('clients:new')}
            onActionClicked={onNewClicked}
          />
        ) : (
          <ClientsTable
            rows={rows}
            expandedId={expandedId}
            onExpandClicked={onExpandClicked}
            onEditClicked={onEditClicked}
            onArchiveClicked={(row) => void onArchiveClicked(row)}
          />
        )}
        {expandedRow !== undefined && (
          <div className="border-t border-border">
            <ClientDetailPanel row={expandedRow} />
          </div>
        )}
      </section>

      <ClientFormDialog
        open={dialogOpen}
        row={editingRow}
        onCloseClicked={onCloseClicked}
      />
    </AppShell>
  )
}
